import React from "react";
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { MobileStepper, Paper, Typography, Button } from "@mui/material";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import styles from "./MobileStepper.module.css";
import { useSwipeable } from "react-swipeable";
import { formatCurrency } from "@_utils/helpers";

export default function TextMobileStepper(props) {
  const { t } = useTranslation();
  const theme = useTheme();
  const caseInfo = useSelector((state) => state.caseDetailsReducer);
  const payment = useSelector((state) => state.paymentReducer);

  let finding = props.finding;
  let country = caseInfo.caseDetails && caseInfo.caseDetails.country;

  const steps = [
    {
      label: `${t("_our_image")}`,
      imgPath: finding.original_image.original_image_url,
    },
    {
      label: `${t("_page_screenshot")}`,
      imgPath: finding.page_screenshot_url,
    },
  ];

  const [activeStep, setActiveStep] = React.useState(0);
  const maxSteps = steps.length;

  const handleNext = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === maxSteps - 1 ? 0 : prevActiveStep + 1,
    );
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) =>
      prevActiveStep === 0 ? maxSteps - 1 : prevActiveStep - 1,
    );
  };

  // Auto-play functionality
  React.useEffect(() => {
    const interval = setInterval(() => {
      handleNext();
    }, 3000); // Change slide every 3 seconds

    return () => clearInterval(interval);
  }, [activeStep]);

  // Swipe handlers
  const swipeHandlers = useSwipeable({
    onSwipedLeft: () => handleNext(),
    onSwipedRight: () => handleBack(),
    preventScrollOnSwipe: true,
    trackMouse: true,
  });

  return (
    <div className={styles.stepper_container}>
      <div>
        <Paper
          square
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            height: 50,
            pl: 2,
            bgcolor: "background.default",
          }}
        >
          <Typography>{steps[activeStep].label}</Typography>
        </Paper>
        <div
          {...swipeHandlers}
          style={{
            overflow: "hidden",
            position: "relative",
          }}
        >
          <div
            style={{
              display: "flex",
              transform: `translateX(-${activeStep * 100}%)`,
              transition: "transform 0.3s ease-in-out",
            }}
            className={styles.stepper_box}
          >
            {steps.map((step, index) => (
              <div key={step.label} className={styles.image_box}>
                <img
                  className={styles.image}
                  src={step.imgPath}
                  alt={step.label}
                />
              </div>
            ))}
          </div>
        </div>
        <MobileStepper
          steps={maxSteps}
          position="static"
          variant="text"
          activeStep={activeStep}
          nextButton={
            <Button
              size="small"
              onClick={handleNext}
              disabled={activeStep === maxSteps - 1}
            >
              {activeStep === 0 ? (
                <p className={styles.btn_text}>{t("_our_image")}</p>
              ) : (
                <></>
              )}
              {activeStep === 1 ? (
                <p className={styles.btn_text}>{t("_page_screenshot")}</p>
              ) : (
                <></>
              )}
              {activeStep === 2 ? <></> : <></>}
              {theme.direction === "rtl" ? (
                <KeyboardArrowLeft />
              ) : (
                <KeyboardArrowRight />
              )}
            </Button>
          }
          backButton={
            <Button
              size="small"
              onClick={handleBack}
              disabled={activeStep === 0}
            >
              {theme.direction === "rtl" ? (
                <KeyboardArrowRight />
              ) : (
                <KeyboardArrowLeft />
              )}
              {activeStep === 0 ? <></> : <></>}
              {activeStep === 1 ? (
                <p className={styles.btn_text}>{t("_our_image")}</p>
              ) : (
                <></>
              )}
              {activeStep === 2 ? (
                <p className={styles.btn_text}>{t("_page_screenshot")}</p>
              ) : (
                <></>
              )}
            </Button>
          }
        />
      </div>
      <div>
        <div>
          {/* license valid */}
          {finding.license && finding.license.state === "VALID" ? (
            <div className={styles.image_text}>
              <p>{t("_uploaded_license_approved")}</p>

              <p>
                {t("_picture_id")}: {props.finding.original_image.filename}
              </p>
            </div>
          ) : (
            <></>
          )}

          {/* license pending */}
          {finding.license &&
          finding.license.state === "UNCHECKED" &&
          caseInfo.caseDetails.state === "ACTIVE" &&
          payment.paymentDetails.state !== "PROCESSING" ? (
            <div className={styles.image_text}>
              <p> {t("_uploaded_license_check_pending")}</p>
              <p>
                {t("_picture_id")}: {props.finding.original_image.filename}
              </p>
            </div>
          ) : (
            <></>
          )}

          {/* no license uploaded || license invalid   */}
          {!finding.license ||
          (finding.license && finding.license.state === "INVALID") ? (
            <div className={styles.image_text}>
              <p>
                {t("_price")}:{" "}
                {formatCurrency(country, props.currency, props.finding.price)}
              </p>
              <p>
                {t("_picture_id")}: {props.finding.original_image.filename}
              </p>
            </div>
          ) : (
            <></>
          )}

          <div className={styles.link_container}>
            <p>
              <a
                className={styles.link}
                href={finding.page_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("_page_link_mobile")}
              </a>
            </p>
            <p>
              <a
                className={styles.link}
                href={finding.image_link}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("_picture_link_mobile")}
              </a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
